import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { PageContext } from "../page-context";

export class HelpView extends BaseView {
    static title = "Help Page";
    static icon = "bi bi-info-circle";

    sectionIds = {
        general: "general",
        resourceAssignments: "resource-assignments",
        projectManagement: "project-management",
        resourceFinder: "resource-finder",
        demands: "demands"
    }

    /**
     * constructor
     * @param {PageContext} context - The context of the page.
     * @param {string} [container=".main"] - The container to render the view in.
     */
    constructor(context: PageContext, container = ".main") {
        super(context, container);
    }

    connectedCallback(): this {
        super.connectedCallback();

        this.init();
        setTimeout(() => {
            this.scrollToSectionOnUrl();
        }, 100);
        return this;
    }

    init() {
        this.innerHTML = `
            <div>
                <h1><i class="${HelpView.icon}"></i> ${HelpView.title}</h1>
                <p>Help page to explain the views and features.</p>

                <div class="help-view-content">
                    <div class="help-view-toc-container">
                        <b>
                            <i class="bi bi-list-stars"></i> Table of contents
                        </b>

                        <ol>
                            <li><u class="btn-general-help cursor-pointer">General process</u></li>
                            <li><u class="btn-resource-assignments-help cursor-pointer">Resource Assignments</u></li>
                            <li><u class="btn-project-managment-help cursor-pointer">Project Managment</u></li>
                            <li><u class="btn-resource-finder-help cursor-pointer">Resource Finder</u></li>
                            <li><u class="btn-demands-help cursor-pointer">Demands</u></li>
                        </ol>
                    </div>

                    <div>
                        <section id="${this.sectionIds.general}" class="help-page-section">
                                <h2>General process</h2>
                                <p>❗Please consult our Sites to understand the general process.</p>
                                <p><b><a href="https://sites.google.com/zoi.de/sites/project-delivery/related-processes-quick-reference-for-projects/resource-demand" target="_blank">Link</a></b></p>
                        </section>
                        <section id="${this.sectionIds.resourceAssignments}" class="help-page-section">
                            <h2>1. Resource Assignment</h2>
                            <p>View resource assignments by employee or project.</p>

                            <div>The input box under <b>Employee / Role</b> can be used to filter data by the <b>Name</b> of Employee:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments--filter.png">

                            <div>You can click on a table's row under <b>Employee / Role</b> & <b>Project</b> to see all the assigned projects of a resource:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments--row.png">
                            
                            <div>After the projects are displayed, if you have appropriate role you can modify the planning hours manually by clicking on the respective cell:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments--manual-planning.png">

                            <div>You can click on a specific project assignment to see/edit its details:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments--opened-row.png">

                            <div>In the <b>Demand assignment details</b> view, you can click on <b>details</b> to go to the <u class="btn-resource-finder-help cursor-pointer">Resource Finder</u> and see all the details of the resource:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments--detail-details.png">
                            
                            <div>In the <b>Demand assignment details</b> view, you can click on <b>Suggestions</b> to open the suggestions popover:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments-detail-suggest-button.png">

                            <div>After the popover is shown, you can see tables that contains individuals who are suggested and those who could potentially be suggested:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments-detail-suggestion-popover.png">

                            <div>
                                If you have the appropriate role, you can do more actions with suggestions popover:
                                <ul>
                                    <li><b>Project Manager</b>: Can view and select the <b>suggest(1)</b> or <b>unsuggest(2)</b> buttons to suggest or unsuggest a person from an assignment.</li>
                                    <li><b>Planner</b>: Can select a person and assign it to demand by clicking on the <b>assign button(3)</b>.</li> 
                                <ul>
                            </div>
                            <img class="help-view-img" src="assets/images/resource-assignments-detail-suggestion-popover-actions.png">

                            <div>At the end of the <b>Demand assignment details</b> view, you can click on <b>FIND RESOURCE</b> button to find resources for current projects who have the matching skills with the demand which in this example is <b>BackendDatabase - MySQL</b>:</div>
                            <img class="help-view-img" src="assets/images/resource-assignments-detail-find-resource.png">

                            <div>
                                If you have the appropriate role, you can do more actions here in this view:
                                <ul>
                                    <li><b>Project Manager</b>: can click on <b>SAVE CHANGES</b> to save the edited info.</li>
                                    <li><b>Planner</b>: Can save and delete the Demand assigment</li>
                                <ul>
                            </div>
                            <img class="help-view-img" src="assets/images/resource-assignments--detail-actions.png">

                        </section>

                        <section id="${this.sectionIds.projectManagement}" class="help-page-section">
                            <h2>2. Project Management</h2>
                            <p>Project Management is a view to help you manage your projects.</p>

                            <div>You can toggle between <b>ALL PROJECTS</b> and <b>MY PROJECTS</b> by clicking on the combox box:</div>
                            <img class="help-view-img" src="assets/images/project-management--toggle-projects.png">

                            <div>The input box under <b>Project / Role</b> can be used to filter data by the <b>Name</b> of Project.</div>
                            <img class="help-view-img" src="assets/images/project-management--filter.png">

                            <div>Similar to <u class="btn-resource-assignments-help cursor-pointer">Resource Assignments</u>, you can click on a table's row under <b>Project / Role</b> & <b>Project</b> to see all the demand assignments of a project and also on a specific <b>Assignment</b> to go into the details.</div>

                        </section>

                        <section id="${this.sectionIds.resourceFinder}" class="help-page-section">
                            <h2>3. Resource Finder</h2>
                            <p>Find resources for your project.</p>

                            <div>You can filter data by the <b>Name</b> of <b>Employee</b> by using the input box under <b>Employee</b>:</div>
                            <img class="help-view-img" src="assets/images/resource-finder--filter.png">

                            <div>For <b>Seniority</b>, <b>Role</b>, <b>Skills</b> columns, you can filter by clicking on the box on then choose multiple options:</div>
                            <img class="help-view-img" src="assets/images/resource-finder--filter-options.png">

                            <div>After the options is shown, you can also type the first letter of the keyword you want to find:</div>
                            <img class="help-view-img" src="assets/images/resource-finder--filter-options-find.png">
                            
                            <div>By clicking on a row you can see the <b>Resource details</b>:</div>
                            <img class="help-view-img" src="assets/images/resource-finder--row.png">
                            
                            <div>In the <b>Resource details</b> view, you can click on <b>FIND ASSIGMENTS</b> to go to <u class="btn-resource-assignments-help cursor-pointer">Resource Assignments</u> and see all the project demands that are assigned to the employee:</div>
                            <img class="help-view-img" src="assets/images/resource-finder--detail.png">

                        </section>

                        <section id="${this.sectionIds.demands}" class="help-page-section">
                            <h2>4. Demands</h2>
                            <p>Demands are the requests for resources to work on projects. They are created by project managers and are assigned to employees.</p>

                            <div>You can toggle between <b>ALL DEMANDS</b> and <b>OPEN DEMANDS</b> by clicking on the combox box:</div>
                            <img class="help-view-img" src="assets/images/demands-toggle-status.png">

                            <div>Similar to the <u class="btn-resource-finder-help cursor-pointer">Resource Finder</u> view, you can filter data in the table by using the input boxs under the column headers.</div>
                            <div>Clicking on a row will also open the <b>Demand assignment details</b> like in <u class="btn-resource-assignments-help cursor-pointer">Resource Assignments</u>.</div>
                        </section>
                    </div>
                </div>
            </div>
        `;

        const elem = $(this);

        elem.find(".btn-resource-assignments-help").click((e) => {
            this.scrollToSection(this.sectionIds.resourceAssignments);
            Navigation.navigateHash(`/help#${this.sectionIds.resourceAssignments}`, undefined, true);
        });

        elem.find(".btn-project-managment-help").click((e) => {
            this.scrollToSection(this.sectionIds.projectManagement);
            Navigation.navigateHash(`/help#${this.sectionIds.projectManagement}`, undefined, true);
        });


        elem.find(".btn-resource-finder-help").click((e) => {
            this.scrollToSection(this.sectionIds.resourceFinder);
            Navigation.navigateHash(`/help#${this.sectionIds.resourceFinder}`, undefined, true);
        });

        elem.find(".btn-demands-help").click((e) => {
            this.scrollToSection(this.sectionIds.demands);
            Navigation.navigateHash(`/help#${this.sectionIds.demands}`, undefined, true);
        });
    }

    scrollToSectionOnUrl() {
        for (const sectionId of Object.values(this.sectionIds)) {
            if (location.hash.endsWith(`help#${sectionId}`)) {
                this.scrollToSection(sectionId)
                return;
            }
        }
    }

    scrollToSection(sectionId: string) {
        const section = document.getElementById(sectionId)
        if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
}
